import type { PDFDocumentProxy } from 'pdfjs-dist';

const PRINT_DPI = 150;
const PDF_UNITS_PER_INCH = 72;

/**
 * Renders every page of the document to an image inside a hidden iframe and
 * opens the browser print dialog. Rendering to images (instead of iframing the
 * PDF itself) works consistently across browsers without relying on a native
 * PDF plugin.
 */
export default async function printPdf(pdf: PDFDocumentProxy): Promise<void> {
    const iframe = document.createElement('iframe');
    iframe.style.position = 'fixed';
    iframe.style.right = '0';
    iframe.style.bottom = '0';
    iframe.style.width = '0';
    iframe.style.height = '0';
    iframe.style.border = 'none';
    iframe.style.visibility = 'hidden';
    document.body.appendChild(iframe);

    const printDocument = iframe.contentDocument;
    const printWindow = iframe.contentWindow;
    if (!printDocument || !printWindow) {
        iframe.remove();
        throw new Error('Unable to create print frame');
    }

    try {
        const style = printDocument.createElement('style');
        style.textContent = `
            @page { size: auto; margin: 0; }
            body { margin: 0; }
            img { display: block; width: 100%; break-inside: avoid; break-after: page; }
        `;
        printDocument.head.appendChild(style);

        const scale = PRINT_DPI / PDF_UNITS_PER_INCH;
        const canvas = document.createElement('canvas');
        for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber++) {
            const page = await pdf.getPage(pageNumber);
            const viewport = page.getViewport({ scale });
            canvas.width = Math.floor(viewport.width);
            canvas.height = Math.floor(viewport.height);
            await page.render({ canvas, viewport, intent: 'print' }).promise;

            const image = printDocument.createElement('img');
            image.src = canvas.toDataURL();
            printDocument.body.appendChild(image);
        }
        // Release the scratch canvas memory.
        canvas.width = 0;
        canvas.height = 0;

        await Promise.all(Array.from(printDocument.images, (image) => image.decode()));

        printWindow.addEventListener('afterprint', () => iframe.remove());
        printWindow.focus();
        printWindow.print();
    } catch (error) {
        iframe.remove();
        throw error;
    }
}
